import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Flame, Trophy, Sparkles, ArrowRight, BarChart3 } from "lucide-react";
import Layout from "../components/Layout";
import BreathingCircle from "../components/BreathingCircle";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { STEPS } from "../data/courseData";
import InspirationCard from "../components/InspirationCard";
import DailyPracticeMiniCard from "../components/DailyPracticeMiniCard";
import BPTICard from "../components/BPTICard";
import PainTrackerCard from "../components/PainTrackerCard";
import DailyRoutineGrid from "../components/DailyRoutineGrid";
import NotificationBell from "../components/NotificationBell";
import logoMark from "../assets/logo-mark.png";

export default function Home() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [identity, setIdentity] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setDash(r.data));
    // Send brand-new users to their Freedom Identity step — but only if they
    // haven't dismissed it this session. Avoid redirect loops.
    if (sessionStorage.getItem("identity-dismissed") === "1") return;
    api.get("/identity").then((r) => {
      if (!r.data) navigate("/identity", { replace: true });
      else setIdentity(r.data);
    }).catch(() => {});
  }, [navigate]);

  const currentStep = STEPS.find((s) => s.step === (dash?.current_step || 1));

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center justify-between">
          <Link to="/settings" data-testid="home-avatar" className="w-11 h-11 rounded-2xl bg-[#0A1530] border border-[#22D3EE]/30 flex items-center justify-center text-[#22D3EE] font-display text-lg shrink-0 hover:border-[#22D3EE]/60 transition-colors">
            {(user?.name?.[0] || "F").toUpperCase()}
          </Link>
          <img
            src={logoMark}
            alt="Freedom From Back Pain"
            data-testid="home-logo-mark"
            className="w-10 h-10 rounded-2xl object-cover drop-shadow-[0_0_18px_rgba(34,211,238,0.45)] select-none"
            draggable={false}
          />
          <NotificationBell dash={dash} />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="bps-section-head text-[#22D3EE]" style={{ textShadow: "0 0 14px rgba(34,211,238,0.5)" }}>Welcome back</div>
            <h1 className="font-display text-3xl font-light tracking-tighter">{user?.name?.split(" ")[0] || "Friend"}</h1>
          </div>
          {dash?.streak > 0 && (
            <div className="flex items-center gap-1.5 bg-[#F59E0B]/15 text-[#F59E0B] px-3 py-1.5 rounded-full text-xs font-medium" data-testid="streak-badge">
              <Flame size={14} strokeWidth={1.8} /> {dash.streak} day{dash.streak === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {/* Pain Tracker + Daily Routine — primary dashboard */}
        <div className="mt-5">
          <PainTrackerCard />
        </div>

        {!dash?.graduated && (
          <div className="mt-6 bps-card overflow-hidden">
            <div className="px-6 pt-5">
              <div className="bps-section-head text-[#F59E0B]" style={{ textShadow: "0 0 14px rgba(245,158,11,0.5)" }}>Course Lessons</div>
            </div>
            <div className="relative px-6 pb-6 pt-3 flex items-center gap-5">
              <BreathingCircle size={140} label="" />
              <div className="flex-1">
                <button
                  onClick={() => navigate(`/journey/${currentStep?.step}`)}
                  data-testid="continue-step-button"
                  className="inline-flex items-center gap-1.5 text-lg font-semibold text-[#22D3EE]"
                >
                  {currentStep?.step === 1 ? "Start here" : "Continue"} <ChevronRight size={20} />
                </button>
                <div className="bps-section-head mt-2" style={{ color: currentStep?.accent, textShadow: `0 0 14px ${currentStep?.accent}80` }}>Week {currentStep?.step}</div>
                <h2 className="font-display text-2xl font-medium tracking-tight mt-1.5">{currentStep?.title}</h2>
                <p className="text-xs text-[#9AAEC6] mt-1 line-clamp-2">{currentStep?.subtitle}</p>
              </div>
            </div>
          </div>
        )}

        {!dash?.graduated && (
          <DailyRoutineGrid
            step={dash?.current_step || 1}
            completionPct={dash?.completion_pct || 0}
            weeksDone={dash?.steps_done || 0}
            activitiesThisWeek={dash?.activities_this_week || 0}
          />
        )}


        {dash?.graduated && (
          <Link to="/graduation" data-testid="graduation-link" className="mt-6 block group">
            <div
              className="relative overflow-hidden rounded-3xl p-6 border border-[#F59E0B]/30 transition-all group-hover:-translate-y-0.5 group-hover:border-[#F59E0B]/50"
              style={{
                background: "linear-gradient(140deg, rgba(245,158,11,0.18) 0%, rgba(34,211,238,0.10) 50%, rgba(10,21,48,0.85) 100%)",
                boxShadow: "0 24px 60px rgba(245,158,11,0.18), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
            >
              {/* Glow halos */}
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.45), transparent 70%)" }} />
              <div className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />

              {/* Confetti sparkles */}
              <Sparkles size={14} className="absolute top-5 right-20 text-[#F59E0B]/80" />
              <Sparkles size={10} className="absolute top-12 right-6 text-[#22D3EE]/70" />
              <Sparkles size={12} className="absolute bottom-6 right-16 text-white/60" />

              <div className="relative flex items-start gap-4">
                {/* Trophy */}
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-2xl blur-xl" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.7), transparent 70%)" }} />
                  <div
                    className="relative w-16 h-16 rounded-2xl flex items-center justify-center bps-pulse-glow"
                    style={{
                      background: "linear-gradient(180deg, #FCD34D 0%, #F59E0B 60%, #B45309 100%)",
                      boxShadow: "0 8px 24px rgba(245,158,11,0.55), inset 0 1px 0 rgba(255,255,255,0.55)",
                    }}
                  >
                    <Trophy size={30} strokeWidth={1.8} className="text-[#3F2A06]" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="bps-eyebrow text-[#F59E0B]">Course Complete</div>
                  <h3 className="font-display text-2xl font-light tracking-tighter mt-1 leading-tight" data-testid="graduation-headline">Congratulations, {user?.name?.split(" ")[0] || "friend"}.</h3>
                  <p className="text-sm text-white/85 mt-2 leading-relaxed">
                    You moved through every layer of <span className="text-white">Reset Mind. Reprogrammed Body.</span>
                  </p>
                </div>
              </div>

              <div className="relative mt-5 flex items-center justify-between gap-3 pt-4 border-t border-white/10">
                <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/55">Awarded {user?.created_at ? new Date(user.created_at).getFullYear() : new Date().getFullYear()}</span>
                <span
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                  style={{
                    background: "linear-gradient(180deg, #FCD34D, #F59E0B)",
                    color: "#3F2A06",
                    boxShadow: "0 0 18px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
                  }}
                  data-testid="view-report-cta"
                >
                  View final report <ArrowRight size={14} strokeWidth={2.4} />
                </span>
              </div>
            </div>
          </Link>
        )}

        {dash?.graduated && (
          <Link
            to="/maintenance-plan"
            data-testid="maintenance-plan-cta"
            className="mt-4 block group"
          >
            <div
              className="relative overflow-hidden rounded-3xl p-5 border border-[#22D3EE]/30 transition-all group-hover:-translate-y-0.5 group-hover:border-[#22D3EE]/50"
              style={{
                background: "linear-gradient(140deg, rgba(34,211,238,0.14) 0%, rgba(10,21,48,0.85) 70%)",
                boxShadow: "0 16px 40px rgba(34,211,238,0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
            >
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />
              <div className="relative flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#22D3EE]/15 border border-[#22D3EE]/40 flex items-center justify-center shrink-0">
                  <Sparkles size={20} className="text-[#22D3EE]" strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bps-eyebrow text-[#22D3EE]">Continue the work</div>
                  <div className="font-display text-base text-white leading-snug mt-0.5">
                    Stay reset with the Maintenance plan
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-0.5">
                    From $4.99 / month
                  </div>
                </div>
                <ArrowRight size={16} className="text-[#22D3EE]" strokeWidth={2.2} />
              </div>
            </div>
          </Link>
        )}

        {!dash?.graduated && (dash?.steps_done || 0) === 0 && <InspirationCard />}

        {identity?.avatar && (
          <Link
            to="/identity"
            data-testid="home-identity-tile"
            className="block mt-6 group"
          >
            <div
              className="relative overflow-hidden rounded-3xl border border-[#22D3EE]/25 p-4 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#22D3EE]/50 transition-all"
              style={{
                background: "linear-gradient(140deg, rgba(34,211,238,0.10) 0%, rgba(10,21,48,0.85) 70%)",
                boxShadow: "0 14px 36px rgba(34,211,238,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
            >
              <div className="absolute -top-10 -right-12 w-32 h-32 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />
              <div className="relative w-14 h-14 rounded-2xl overflow-hidden border border-[#22D3EE]/40 shrink-0">
                <img src={identity.avatar} alt="" className="w-full h-full object-cover" draggable={false} />
              </div>
              <div className="relative flex-1 min-w-0">
                <div className="bps-eyebrow text-[#22D3EE]">My freedom identity</div>
                <div className="font-display text-base text-white leading-snug mt-0.5 line-clamp-1">
                  {identity.who}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-0.5">
                  Tap to revisit
                </div>
              </div>
              <ChevronRight size={16} className="text-[#22D3EE]" />
            </div>
          </Link>
        )}

        {!dash?.graduated && currentStep?.step && (
          <DailyPracticeMiniCard stepNum={currentStep.step} className="mt-4" />
        )}

        <Link
          to="/weekly-report"
          data-testid="home-weekly-report-link"
          className="mt-3 block group"
        >
          <div
            className="relative overflow-hidden rounded-3xl border border-[#22D3EE]/25 p-4 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#22D3EE]/55 transition-all"
            style={{
              background: "linear-gradient(140deg, rgba(34,211,238,0.10) 0%, rgba(245,158,11,0.04) 50%, rgba(10,21,48,0.85) 100%)",
              boxShadow: "0 14px 36px rgba(34,211,238,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />
            <div
              className="relative w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: "linear-gradient(140deg, #38BDF8, #22D3EE 45%, #A855F7)",
                border: "1px solid rgba(56,189,248,0.8)",
                boxShadow: "0 0 22px rgba(34,211,238,0.7), inset 0 1px 0 rgba(255,255,255,0.3)",
              }}
            >
              <BarChart3 size={22} className="text-white" strokeWidth={2} style={{ filter: "drop-shadow(0 0 6px rgba(34,211,238,0.9))" }} />
            </div>
            <div className="relative flex-1 min-w-0">
              <div className="bps-eyebrow text-[#22D3EE]">This week</div>
              <div className="font-display text-base text-white leading-snug mt-0.5">
                Weekly activity report
              </div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-0.5">
                By day · By category · Top habits
              </div>
            </div>
            <ChevronRight size={16} className="text-[#22D3EE]" />
          </div>
        </Link>

        <BPTICard />

        <div
          className="mt-6 relative overflow-hidden rounded-3xl p-5 border border-[#22D3EE]/20"
          style={{
            background:
              "radial-gradient(120% 90% at 0% 0%, rgba(56,189,248,0.16), transparent 55%), radial-gradient(110% 90% at 100% 100%, rgba(168,85,247,0.14), transparent 55%), linear-gradient(160deg, rgba(10,21,48,0.9) 0%, rgba(3,16,31,0.96) 100%)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
          data-testid="daily-checkin-card"
        >
          <DailyCheckin onSaved={() => api.get("/me/dashboard").then((r) => setDash(r.data))} />
        </div>
      </motion.div>
    </Layout>
  );
}

function DailyCheckin({ onSaved }) {
  const [pain, setPain] = useState(5);
  const [emo, setEmo] = useState(5);
  const [align, setAlign] = useState(5);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await api.post("/daily-checkin", { pain_score: pain, emotional_score: emo, alignment_score: align });
      setSaved(true);
      onSaved?.();
    } finally { setSaving(false); }
  }

  if (saved) return (
    <div className="flex flex-col items-center justify-center py-4 text-center" data-testid="checkin-saved">
      <div className="w-12 h-12 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mb-2" style={{ boxShadow: "0 0 22px rgba(16,185,129,0.45)" }}>
        <Sparkles size={20} className="text-[#10B981]" />
      </div>
      <div className="text-sm font-medium text-[#10B981]">Check-in saved for today.</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bps-section-head text-[#38BDF8]" style={{ textShadow: "0 0 14px rgba(56,189,248,0.5)" }}>Daily Check-in</div>
      <ScoreInput label="Pain right now" value={pain} setValue={setPain} testid="checkin-pain" accent="#F59E0B" />
      <ScoreInput label="Emotional state" value={emo} setValue={setEmo} testid="checkin-emotion" accent="#38BDF8" />
      <ScoreInput label="Mind–body alignment" value={align} setValue={setAlign} testid="checkin-alignment" accent="#34C759" />
      <button
        onClick={submit}
        disabled={saving}
        data-testid="checkin-submit"
        className="w-full py-3 rounded-full text-[#03101F] text-sm font-bold disabled:opacity-40 transition-transform hover:-translate-y-0.5"
        style={{ background: "linear-gradient(90deg, #38BDF8, #22D3EE, #34C759)", boxShadow: "0 0 24px rgba(34,211,238,0.45)" }}
      >
        {saving ? "Saving…" : "Save today's check-in"}
      </button>
    </div>
  );
}

function ScoreInput({ label, value, setValue, testid, accent = "#22D3EE" }) {
  return (
    <div data-testid={testid} className="rounded-2xl p-3 border" style={{ borderColor: `${accent}33`, background: `${accent}0D` }}>
      <div className="flex justify-between items-center text-xs mb-1.5">
        <span className="inline-flex items-center gap-2 text-[#C7D6EA]">
          <span className="w-2 h-2 rounded-full" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
          {label}
        </span>
        <span className="font-display font-bold px-2.5 py-0.5 rounded-full" style={{ color: accent, background: `${accent}1f`, border: `1px solid ${accent}55` }}>{value}/10</span>
      </div>
      <input
        type="range" min={0} max={10} step={1} value={value}
        onChange={(e) => setValue(parseInt(e.target.value))}
        className="w-full"
        style={{ accentColor: accent }}
      />
      <div className="flex justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] mt-1">
        <span>Low</span><span>High</span>
      </div>
    </div>
  );
}
